import React, { createRef, MutableRefObject, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import HeadBlock from "../../components/HeadBlock";
import Header from "../../components/Header";
import Nav from "../../components/Nav";
import { useAuthContext } from "../../components/AuthContext";
import { Button } from "../../components/Button";
import { ContentsView } from "../../components/ContentsView";
import { SigninModal } from "../../components/SigninModal";
import { MyContentCard } from "./MyContentCard";
import { useMyContents } from "../../hooks/useMyContents";
import { usePaymentHandling } from "../../hooks/usePaymentHandling";
import { useContentModal } from "../../hooks/useContentModal";
import { useUnpurchasedContents } from "../../hooks/useUnpurchasedContents";
import styles from "../../assets/styles/myContents.module.scss";
import stylesLoading from "../../assets/styles/loading.module.scss";

function MyContentsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isSubscribed } = useAuthContext();
  const containerRef = createRef() as MutableRefObject<HTMLDivElement>;
  const [shownModalSignin, setShownModalSignin] = useState<string | null>(null);

  // カスタムフックを使用して状態管理を分離
  const {
    contents,
    purchasedContents,
    loading,
    finishedFetch,
    refreshContents,
  } = useMyContents();

  // サブスクユーザー用の未購入コンテンツ
  const { unpurchasedContents, loading: unpurchasedLoading } =
    useUnpurchasedContents();

  const { isLoadingPurchase, isShownModalCompleted, setIsShownModalCompleted } =
    usePaymentHandling(finishedFetch, refreshContents);

  const { showContentsModal, setShowContentsModal, isLoadingVideo } =
    useContentModal(purchasedContents, unpurchasedContents);

  const handlers = useSwipeable({
    onSwiped: (event) => {
      if (event.dir === "Up" || event.dir === "Down") {
        navigate(`/my-contents?id=${searchParams.get("id")}`, {
          replace: true,
        });
      }
    },
    trackMouse: true,
  });

  if (loading) {
    return (
      <>
        <HeadBlock title={"購入作品"} path="my-contents" />
        <Header />
        <div className={styles.MyContents}>
          <div className={stylesLoading.loading}>
            <p>Loading...</p>
          </div>
        </div>
        <Nav />
      </>
    );
  }

  return (
    <>
      <HeadBlock title={"購入作品"} path="my-contents" />
      <Header />
      <div className={styles.MyContents}>
        {!user ? (
          <div className={styles.non_login}>
            <img
              src={require("../../assets/images/non-login_mycontents.png")}
              alt=""
            />
            <h2>購入した作品や、聴き放題可能な作品が追加されます</h2>
            <button type="button" onClick={() => navigate("/signin")}>
              ログイン / 登録
            </button>
          </div>
        ) : purchasedContents.length > 0 ||
          (isSubscribed && unpurchasedContents.length > 0) ? (
          <div className={styles.inner}>
            <h1>マイリスト</h1>
            <p className={styles.description}>
              作品を購入、または聴き放題プランにご登録の場合、閲覧可能な作品がマイリストに表示されます
            </p>
            <div style={{ position: "relative" }}>
              <ul className={styles.libraries}>
                {/* 購入済みコンテンツを表示 */}
                {purchasedContents.map((c) => (
                  <li key={`purchased-${c.id}`}>
                    <MyContentCard content={c} isPurchased />
                  </li>
                ))}

                {/* サブスクユーザーの場合、未購入コンテンツも同じリストに表示 */}
                {isSubscribed &&
                  !unpurchasedLoading &&
                  unpurchasedContents.map((c) => (
                    <li key={`unpurchased-${c.id}`}>
                      <MyContentCard content={c} />
                    </li>
                  ))}
              </ul>

              {/* 未購入コンテンツのローディング表示 */}
              {isSubscribed && unpurchasedLoading && (
                <div className={stylesLoading.loading}>
                  <p>Loading...</p>
                </div>
              )}
            </div>

            {!isSubscribed && (
              <div className={styles.btn_wrap}>
                <Button variant="secondary" onClick={() => navigate("/")}>
                  <span>他の作品を探す</span>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.empty}>
            <img
              src={require("../../assets/images/empty_mycontents.png")}
              alt=""
            />
            <h2>
              まだ視聴可能な作品が
              <br />
              ありません
            </h2>
            <p>作品を購入、または聴き放題プランにご登録の場合、</p>
            <p>閲覧可能な作品がマイリスト表示されます</p>

            <button type="button" onClick={() => navigate("/")}>
              作品を探す
            </button>
          </div>
        )}

        {isLoadingPurchase && (
          <div className={styles.loadingPurchase}>
            <div className={styles.inner}>
              <p>購入処理中...</p>
            </div>
          </div>
        )}

        {isShownModalCompleted && (
          <div className={styles.modal}>
            <div className={styles.modalInner}>
              <h2>購入が完了しました！</h2>
              <p>さっそく作品を視聴してみましょう！</p>
              <iframe
                id="iframe"
                src=""
                title="app"
                style={{ display: "none" }}
              />
              <div className={styles.btns}>
                <Button onClick={() => setIsShownModalCompleted(false)}>
                  OK
                </Button>
              </div>
            </div>
            <div
              className={styles.overlay}
              onClick={() => setIsShownModalCompleted(false)}
            ></div>
          </div>
        )}

        {isLoadingVideo && (
          <div className={styles.loading_video}>
            <p>読み込み中...</p>
          </div>
        )}
      </div>
      <Nav />

      {showContentsModal && (
        <div ref={containerRef}>
          <div {...handlers}>
            <ContentsView
              containerRef={containerRef}
              c={
                [...contents, ...unpurchasedContents].filter(
                  (c) => c.id === showContentsModal,
                )[0]
              }
              currentContentId={showContentsModal}
              purchasedContent={
                purchasedContents
                  .filter((c) => c.id === showContentsModal)
                  .map((c) => {
                    return {
                      id: c.id,
                      chapters: c.chapters.map((ch) => ch.index),
                    };
                  })[0]
              }
              setShowContentsModal={setShowContentsModal}
            />
          </div>
        </div>
      )}

      {shownModalSignin && (
        <SigninModal
          setShownModalSignin={setShownModalSignin}
          shownModalSignin={shownModalSignin}
          chapter={[Number(searchParams.get("chapter"))]}
        />
      )}
    </>
  );
}

export default MyContentsList;
